import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import { FileRejection } from 'react-dropzone';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ImageGallery from 'inquiry/components/ImageGallery';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepDocumentStyleContext } from 'inquiry/context/StepDocumentStyleContext';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import { DocumentFile } from 'verify/models/document-file';
import { DocumentUpload } from 'verify/redux/reducers/document';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

/**
 * https://withpersona.atlassian.net/browse/PER-16477
 * The default position relative button styling was causing submit button to
 * render over the capture options dialog on iOS Safari 16 override this by
 * using position: static. This shouldn't affect anything in this case since we
 * aren't setting top/bottom/left/right css properties.
 *
 * If this fix causes trouble elsewhere, the other possible fix was to move the
 * capture options dialog one level higher in the DOM, but that required moving
 * a ton of code around, so sticking to this for now
 */
const SubmitButton = styled(Button)`
  position: static;
`;

interface DocumentsReviewProps {
  allowedCaptureOptions: Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile>;
  acceptedFileTypes: StepDocumentConfig['accepted-file-types'];
  assets: StepDocumentConfig['assets']['prompt-page'];
  documentFiles: DocumentFile[];
  documentFileLimit: number;
  handleAddError: (files: FileRejection[]) => void;
  handleAddFiles: (files: File[]) => Promise<void>;
  handleDeviceHandoff: () => void;
  handleRemoveFile: (id: string) => Promise<void>;
  handleSelectCamera: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  loading: boolean;
  localizations: StepDocumentConfig['localizations']['prompt-page'];
  submitting: boolean;
  uploads: DocumentUpload[];
  pendingDeletions: string[];
}

/**
 * View rendered when no files have been uploaded to document.
 */
export default function DocumentsReview({
  allowedCaptureOptions,
  acceptedFileTypes,
  assets,
  documentFiles,
  documentFileLimit,
  handleAddError,
  handleAddFiles,
  handleDeviceHandoff,
  handleRemoveFile,
  handleSelectCamera,
  handleSubmit,
  loading,
  localizations,
  submitting,
  uploads,
  pendingDeletions,
}: DocumentsReviewProps) {
  const styles = useStepDocumentStyleContext();
  const { base, buttons: buttonStyles } = styles;

  // Only allow submit if at least 1 uploaded file and no pending files
  // Don't enable submit if we have pending deletions because this
  // would let someone queue a deletion and submit a doc verification with no files.
  const canSubmit =
    documentFiles.length > 0 &&
    uploads.length === 0 &&
    pendingDeletions.length === 0 &&
    !loading &&
    !submitting;

  const files = useMemo<React.ComponentProps<typeof ImageGallery>['files']>(() => {
    const uploadedFiles = documentFiles.map(({ id: fileId, attributes: { name, originals } }) => ({
      type: 'uploaded' as const,
      filename: name,
      // Will only have a single file for now.
      url: originals[0].url,
      fileType: originals[0]['file-type'],
      id: fileId,
      deleting: pendingDeletions.includes(fileId),
    }));

    const uploadingFiles = uploads.map((upload) => ({
      type: 'uploading' as const,
      filename: upload.file.filename,
      url: upload.file.dataUrl,
      fileType: upload.file.fileType,
      progress: upload.progress,
      id: String(upload.id),
    }));

    return [...uploadedFiles, ...uploadingFiles];
  }, [documentFiles, uploads, pendingDeletions]);

  return (
    <Container data-test='documents-review'>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations.title}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{localizations.prompt}</Markdown>
        </ThemedParagraph>
      </Header>
      <ImageGallery
        data-test='documents-review__image-gallery'
        disabled={loading}
        // Disable ability to add new files if we have pending uploads or deletions
        // this is because these operations make a call to refresh the documents list,
        // which could lead to race conditions where a file is rendered as both added
        // and pending at the same time.
        //
        // Also disables if the total file count >= the limit. We don't need to
        // consider uploads or pending deletings because any existing will
        // already disable adding files.
        addFileDisabled={
          uploads.length > 0 || pendingDeletions.length > 0 || files.length >= documentFileLimit
        }
        allowedCaptureOptions={allowedCaptureOptions}
        acceptedImageFormats={acceptedFileTypes}
        files={files}
        onAddClick={handleSelectCamera}
        onAddError={handleAddError}
        onChange={handleAddFiles}
        onDeviceHandoff={handleDeviceHandoff}
        onRemove={handleRemoveFile}
        captureOptionsDialogTitle={localizations['capture-options-dialog-title']}
        webCameraBtnLabel={localizations['btn-capture']}
        mobileCameraBtnLabel={localizations['btn-capture']}
        uploadBtnLabel={localizations['btn-upload']}
        deviceHandoffBtnLabel={localizations['btn-device-handoff']}
        deleteDialogTitle={localizations['delete-dialog-title']}
        cancelBtnLabel={localizations['btn-cancel']}
        deleteBtnLabel={localizations['btn-delete']}
        noCaptureOptionsPrompt={localizations['no-capture-options-prompt']}
        styles={styles}
      />
      <Spacer height={4} />
      <PositionableButtonContainer>
        <SubmitButton
          id='documents-review__btn-submit'
          data-test='documents-review__btn-submit'
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          onClick={handleSubmit}
          isPending={submitting}
          disabled={!canSubmit}
        >
          {localizations['btn-submit']}
        </SubmitButton>
      </PositionableButtonContainer>
    </Container>
  );
}
